<template>
  <div class="health-report-view">
    <div class="report-toolbar">
      <div class="report-toolbar-info">
        <span class="report-toolbar-title">个人健康信息报告</span>
        <span class="report-toolbar-description">A4 预览 · 共 1 页</span>
      </div>
      <button
        class="report-download-button"
        type="button"
        :disabled="downloading"
        :aria-busy="downloading ? 'true' : 'false'"
        @click="downloadPDF"
      >
        {{ downloading ? '正在生成 PDF…' : '一键下载 PDF' }}
      </button>
    </div>

    <p v-if="downloadError" class="report-download-error" role="alert">
      {{ downloadError }}
    </p>
    <p class="report-download-status" role="status" aria-live="polite">
      {{ downloadStatus }}
    </p>

    <div class="report-preview" aria-label="个人健康信息报告预览">
      <article ref="reportPage" class="report-page">
        <header class="report-cover">
          <p class="report-cover-label">PERSONAL HEALTH REPORT</p>
          <h1 class="report-cover-title">个人健康信息报告</h1>
          <p class="report-cover-subtitle">从风险识别到每日行动</p>
          <p class="report-cover-meta">
            <span>报告日期&nbsp; {{ report.reportDate }}</span>
            <span class="report-meta-divider" aria-hidden="true">|</span>
            <span>{{ report.dataStatus }}</span>
          </p>
        </header>

        <table class="report-info-table" aria-label="基本信息与健康资料">
          <colgroup>
            <col class="report-basic-column" />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">基本信息</th>
              <th scope="col">健康资料</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>姓名：{{ report.name }} / 性别：{{ report.gender }}</td>
              <td>专属健康管理师：{{ report.healthManager }}</td>
            </tr>
            <tr>
              <td>年龄：{{ report.age }}岁</td>
              <td>手机号：{{ report.phone }}</td>
            </tr>
            <tr>
              <td>身高：{{ report.height }} cm</td>
              <td>体重记录：{{ report.weightRecord }}；{{ report.weightNote }}</td>
            </tr>
            <tr>
              <td>数据来源</td>
              <td>{{ report.dataSource }}</td>
            </tr>
          </tbody>
        </table>

        <section class="report-focus" aria-labelledby="report-focus-title">
          <h2 id="report-focus-title" class="report-focus-title">本报告关注重点</h2>
          <p class="report-focus-content">{{ report.focus }}</p>
        </section>

        <footer class="report-footer">
          <span>个人健康资料 · 具体诊疗以医生评估为准</span>
          <span>01 / 01</span>
        </footer>
      </article>
    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas'
import { jsPDF as JsPDF } from 'jspdf'

const PAGE_WIDTH = 794
const PAGE_HEIGHT = 1123

// 当前仅实现提供的报告首页；后续可通过 reportData 传入实际健康资料。
const DEFAULT_REPORT = {
  reportDate: '2026-10-10',
  dataStatus: '数据未更新',
  name: '张长春',
  gender: '男',
  age: 51,
  riskAge: 52,
  healthManager: '戴婷婷',
  phone: '13851506009',
  height: 165,
  weightRecord: '65 kg / 67.5 kg',
  weightNote: '2026-10-12',
  dataSource: '美年大体检中心、4G血压计、血糖仪、4G星18手表',
  focus: '优先管理缺血性脑卒中高风险、缺血性心脏病中风险，以及糖尿病、高血压、血脂异常与脂肪肝的综合风险。'
}

export default {
  name: 'PdfDownLoade',
  props: {
    reportData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      downloading: false,
      downloadError: '',
      downloadStatus: ''
    }
  },
  computed: {
    report() {
      return { ...DEFAULT_REPORT, ...this.reportData }
    }
  },
  created() {
    this.wxUserId = this.$route.query.wxUserId
  },
  methods: {
    async downloadPDF() {
      if (this.downloading) return

      this.downloading = true
      this.downloadError = ''
      this.downloadStatus = ''
      let canvas = null

      try {
        await this.$nextTick()
        // 等待字体加载，防止中文在导出时被替换或缺失。
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready
        }

        const page = this.$refs.reportPage
        if (!page) throw new Error('报告尚未加载')

        // 在绘制前固定内容快照，让文件名与导出的报告一致。
        const filename = `个人健康信息报告_${this.report.name}_${this.report.reportDate}.pdf`
          .replace(/[\\/:*?"<>|]/g, '_')

        canvas = await html2canvas(page, {
          scale: 2,
          width: PAGE_WIDTH,
          height: PAGE_HEIGHT,
          windowWidth: Math.max(PAGE_WIDTH, document.documentElement.clientWidth),
          windowHeight: PAGE_HEIGHT,
          scrollX: 0,
          scrollY: 0,
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
          onclone: (clonedDocument, clonedPage) => {
            // 移出滚动容器，仅调整副本，避免窄屏或父容器裁切 PDF。
            clonedDocument.body.appendChild(clonedPage)
            clonedDocument.body.style.margin = '0'
            clonedPage.style.position = 'absolute'
            clonedPage.style.left = '0'
            clonedPage.style.top = '0'
            clonedPage.style.margin = '0'
            clonedPage.style.transform = 'none'
            clonedPage.style.boxShadow = 'none'
          }
        })

        const pdf = new JsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true
        })
        pdf.setProperties({ title: '个人健康信息报告', subject: '个人健康资料' })
        // 整页写入 A4，保证中文、表格和页脚与预览一致，不产生多余空白页。
        pdf.addImage(
          canvas,
          'PNG',
          0,
          0,
          pdf.internal.pageSize.getWidth(),
          pdf.internal.pageSize.getHeight(),
          undefined,
          'FAST'
        )
        await pdf.save(filename, { returnPromise: true })
        this.downloadStatus = 'PDF 已生成，已发起下载。'
      } catch (error) {
        this.downloadError = 'PDF 生成失败，请稍后重试。'
      } finally {
        if (canvas) {
          canvas.width = 0
          canvas.height = 0
        }
        this.downloading = false
      }
    }
  }
}
</script>

<style scoped>
.health-report-view {
  padding: 24px;
  background: #eef2f5;
  color: #173e56;
  font-family: Arial, 'Microsoft YaHei', 'PingFang SC', sans-serif;
}

.health-report-view,
.health-report-view *,
.report-page,
.report-page * {
  box-sizing: border-box;
}

.report-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  max-width: 794px;
  margin: 0 auto 20px;
}

.report-toolbar-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.report-toolbar-title {
  font-size: 18px;
  font-weight: 700;
}

.report-toolbar-description {
  color: #718797;
  font-size: 13px;
}

.report-download-button {
  flex-shrink: 0;
  min-width: 136px;
  min-height: 42px;
  padding: 10px 18px;
  border: 1px solid #078693;
  border-radius: 6px;
  background: #078693;
  color: #ffffff;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}

.report-download-button:hover {
  border-color: #066e79;
  background: #066e79;
}

.report-download-button:focus-visible {
  outline: 3px solid #9ad1d6;
  outline-offset: 3px;
}

.report-download-button:disabled {
  opacity: 0.65;
  cursor: wait;
}

.report-download-error {
  max-width: 794px;
  margin: 0 auto 16px;
  color: #b63838;
  font-size: 14px;
}

.report-download-status {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

.report-preview {
  overflow-x: auto;
  padding-bottom: 8px;
}

.report-page {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 794px;
  height: 1123px;
  margin: 0 auto;
  padding: 78px 58px 38px;
  box-sizing: border-box;
  background: #ffffff;
  box-shadow: 0 3px 18px rgba(23, 62, 86, 0.08);
  color: #173e56;
  font-family: Arial, 'Microsoft YaHei', 'PingFang SC', sans-serif;
  font-size: 13px;
  line-height: 1.6;
  text-align: left;
}

.report-cover {
  flex-shrink: 0;
  height: 318px;
  padding: 30px;
  border-radius: 14px;
  background: #f2f7f8;
}

.report-cover-label {
  margin: 0;
  color: #078693;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

.report-cover-title {
  margin: 28px 0 8px;
  color: #173e56;
  font-size: 40px;
  font-weight: 700;
  line-height: 56px;
  letter-spacing: 0;
}

.report-cover-subtitle {
  margin: 0;
  font-size: 18px;
  line-height: 27px;
}

.report-cover-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 64px 0 0;
  color: #6689a1;
  font-size: 12px;
  line-height: 20px;
}

.report-meta-divider {
  color: #7b94a4;
}

.report-info-table {
  flex-shrink: 0;
  width: 100%;
  margin-top: 32px;
  border: 1px solid #d1e1e8;
  border-collapse: collapse;
  table-layout: fixed;
  color: #173e56;
  font-size: 13px;
  line-height: 20px;
}

.report-basic-column {
  width: 47%;
}

.report-info-table th {
  height: 44px;
  padding: 10px 12px;
  border: 0;
  background: #193b4d;
  color: #ffffff;
  font-weight: 700;
  text-align: left;
  vertical-align: middle;
}

.report-info-table td {
  height: 41px;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid #d1e1e8;
  background: #ffffff;
  overflow-wrap: break-word;
  vertical-align: middle;
}

.report-info-table tbody tr:nth-child(even) td {
  background: #f2f7f8;
}

.report-focus {
  position: relative;
  flex-shrink: 0;
  margin-top: 27px;
  padding: 13px 20px 18px;
  border-radius: 10px;
  background: #f2f7f8;
}

.report-focus::before {
  position: absolute;
  top: 11px;
  bottom: 11px;
  left: 0;
  width: 5px;
  background: #078693;
  content: '';
}

.report-focus-title {
  margin: 0 0 10px;
  color: #078693;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.report-focus-content {
  margin: 0;
  font-size: 14px;
  line-height: 23px;
}

.report-footer {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #cfdee6;
  color: #6689a1;
  font-size: 10px;
  line-height: 16px;
}

@media (max-width: 600px) {
  .health-report-view {
    padding: 16px 12px;
  }

  .report-toolbar {
    align-items: flex-start;
    flex-wrap: wrap;
  }
}

@media print {
  .health-report-view {
    padding: 0;
    background: #ffffff;
  }

  .report-toolbar,
  .report-download-error,
  .report-download-status {
    display: none;
  }

  .report-preview {
    overflow: visible;
    padding: 0;
  }

  .report-page {
    margin: 0;
    box-shadow: none;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
</style>
